import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Instagram, Send, Check } from "lucide-react";
import { toast } from "sonner";
import Logo from "./Logo";
import { sendForm } from "../lib/forms";
import { HELPLINE_NOTE, MARQUEE_ITEMS } from "../data/content";

const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
  </svg>
);

const SOCIALS = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/back_of_mind_?mdxt=MWp3N3pzY2lndzg3NQ==",
    Icon: Instagram,
    testid: "footer-social-instagram",
  },
  {
    label: "X (formerly Twitter)",
    href: "https://x.com/backofthemind_?s=21",
    Icon: XLogo,
    testid: "footer-social-x",
  },
];

const COLS = [
  {
    title: "Explore",
    links: [
      { to: "/individual-healing", label: "Individual Healing" },
      { to: "/corporate-solutions", label: "Corporate Solutions" },
      { to: "/quick-heals", label: "Quick Heals" },
      { to: "/store", label: "Digital Products" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/insights", label: "Insight Blogs" },
      { to: "/faqs", label: "FAQs" },
      { to: "/contact", label: "Contact & Booking" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await sendForm("Newsletter signup", { email });
      setDone(true);
      toast.success("Welcome to the trail. First letter arrives soon.");
      setEmail("");
    } catch {
      toast.error("Couldn't send right now — please email connectbackofthemind@gmail.com.");
    }
  };

  return (
    <footer className="relative overflow-hidden bg-pinedeep text-cream">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-[1.3fr_1fr_1fr_1.4fr]">
          <div>
            <Logo light />
            <p className="mt-5 max-w-xs font-serif text-2xl italic leading-snug text-cream/90">
              Look inward. Understand what shapes you.
            </p>
            <div className="mt-6 flex gap-3">
              {SOCIALS.map(({ label, href, Icon, testid }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={testid}
                  className="rounded-full border border-cream/20 p-2.5 text-cream/80 transition-all duration-300 hover:border-blush hover:text-blush"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sage">
                {col.title}
              </p>
              <ul className="mt-5 space-y-3">
                {col.links.map((l) => (
                  <li key={l.to + l.label}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="link-underline text-sm text-cream/75 hover:text-cream"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div data-testid="footer-newsletter">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sage">
              Letters from the mountain
            </p>
            <p className="mt-4 text-sm text-cream/70">
              One short, useful note on feeling lighter — twice a month. No noise, ever.
            </p>
            {done ? (
              <p
                data-testid="footer-newsletter-success"
                className="mt-5 flex items-center gap-2 text-sm text-blush"
              >
                <Check className="h-4 w-4" /> You're on the list. See you in your inbox.
              </p>
            ) : (
              <form onSubmit={subscribe} className="mt-5 flex gap-2">
                <label htmlFor="footer-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="footer-email"
                  data-testid="footer-newsletter-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-soft border-cream/20 bg-white/5 text-cream placeholder:text-cream/35"
                />
                <button
                  data-testid="footer-newsletter-submit"
                  type="submit"
                  aria-label="Subscribe to newsletter"
                  className="flex h-11 w-12 shrink-0 items-center justify-center rounded-2xl bg-terra text-cream transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>

        <div
          data-testid="footer-crisis-card"
          className="mt-16 rounded-3xl border border-terra/30 bg-terra/10 p-6"
        >
          <p className="text-sm leading-relaxed text-cream/90">{HELPLINE_NOTE}</p>
          <a
            data-testid="footer-crisis-link"
            href="https://findahelpline.com"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-blush hover:text-white"
          >
            findahelpline.com <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>

        <div className="mt-10 flex flex-col gap-3 border-t border-cream/10 pt-6 text-xs text-cream/50 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Back Of The Mind. All rights reserved.</p>
          <p className="italic">
            Coaching and NLP-based support, not licensed clinical care.
          </p>
        </div>
      </div>

      <div aria-hidden="true" className="pointer-events-none absolute -bottom-8 left-0 right-0 overflow-hidden whitespace-nowrap opacity-[0.06]">
        <p className="pl-2 font-serif text-[13vw] leading-none">
          {MARQUEE_ITEMS.join(" · ")}
        </p>
      </div>
    </footer>
  );
}
